import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SectionContainer, SectionLabel, SectionHeading, SectionLead } from "./SectionPrimitives";

export const FAQS = [
  {
    q: "Was kostet ein Audit?",
    a: "Das Erst-Audit ist kostenfrei und dauert von Anfrage bis fertigem Report 5 Werktage. Wir prüfen Datenqualität, Feed-Validität und SEO-Befund für Ihren kompletten Bestand und liefern einen strukturierten PDF-Report inklusive 30-Min Strategie-Call.",
  },
  {
    q: "Wie lange dauert das Service-Setup nach Beauftragung?",
    a: "In der Regel 14–21 Tage. Wir analysieren Quellen, bauen die Datenpipeline, validieren Pflichtfelder und gehen mit Feed + SEO-Layer live. Bei größeren Händlergruppen kalkulieren wir Setup-Phasen pro Standort.",
  },
  {
    q: "Welche DMS- und Bestandsquellen sind anschließbar?",
    a: "Alle gängigen Systeme: Modix, AutoBiz, AHAG, Würth, mobile.de, Autoscout24, OEM-Feeds, IMS-Plattformen. Bei proprietären Systemen prüfen wir Schnittstellen (SFTP, REST, Datei-Export) und richten den Import individuell ein.",
  },
  {
    q: "Wer betreibt die Infrastruktur — Sie oder wir?",
    a: "Wir betreiben die komplette Infrastruktur (Crawler, Validierung, Anreicherung, Feed-Engine, SEO-Generator) — Sie erhalten Reportings, Zugriff auf Audit-Berichte und gegebenenfalls ein Steuerungs-Dashboard. Hosting erfolgt DSGVO-konform in der EU.",
  },
  {
    q: "Welche Daten reichern Sie konkret an?",
    a: "Pro Fahrzeug bis zu 120 Datenpunkte: technische Daten, Ausstattung, Verbrauchswerte (WLTP), Effizienz-Klasse, Bilder-Tags, Vergleichs-Modelle, lokale Marktdaten. Quellen werden referenziert und sind im Audit-Trail nachvollziehbar.",
  },
  {
    q: "Wie wird der Erfolg gemessen?",
    a: "Monatliches Reporting mit Vorher-/Nachher-Vergleich: Feed-Score, Sichtbarkeits-Index, indexierte URLs, Klick-Reichweite, Disapproval-Quote und qualifizierte Leads. Auf Wunsch koppeln wir Conversion-Tracking aus Google Ads und Analytics direkt ein.",
  },
  {
    q: "Können auch Agenturen den Service einsetzen?",
    a: "Ja. Unser White-Label-Modell stellt Agenturen und Plattformen die komplette Infrastruktur unter eigener Marke zur Verfügung — inklusive eigener Reports, Mengenstaffeln und Co-Branding-Optionen.",
  },
  {
    q: "Wie unterscheidet sich der Service von einem reinen Feed-Tool?",
    a: "Reine Feed-Tools validieren — wir verantworten den gesamten Prozess von Rohdaten bis zur SEO-Landingpage. Sie müssen nichts selbst bauen, konfigurieren oder pflegen. Wir liefern Datenqualität, Sichtbarkeit und Reporting als Ergebnis.",
  },
];

export default function FAQ() {
  return (
    <SectionContainer id="faq" className="bg-white">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionLabel number="/ 11" label="FAQ" />
          <SectionHeading>Häufig gestellte Fragen.</SectionHeading>
          <SectionLead>
            Antworten auf die Themen, die in den meisten Erstgesprächen aufkommen. Falls eine
            Frage offen bleibt — wir melden uns innerhalb von 24h.
          </SectionLead>

          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-lg p-6">
            <p className="label-mono text-slate-500">Sie haben eine andere Frage?</p>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              Schreiben Sie uns kurz, was zu klären ist — wir antworten als Ansprechpartner
              aus dem Service-Team, nicht als generischer Helpdesk.
            </p>
            <Link
              to="/audit"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-cyan-700 hover:text-cyan-800"
            >
              Frage stellen <ArrowUpRight className="size-3.5" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Accordion type="single" collapsible defaultValue="item-0" className="border border-slate-200 rounded-lg bg-white overflow-hidden divide-y divide-slate-200">
            {FAQS.map((f, idx) => (
              <AccordionItem key={f.q} value={`item-${idx}`} className="border-b-0">
                <AccordionTrigger
                  data-testid={`faq-${idx}`}
                  data-track="faq_toggle"
                  data-question={f.q}
                  className="px-5 sm:px-6 py-5 text-left font-display font-medium text-slate-900 hover:no-underline hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-start gap-4">
                    <span className="font-mono text-[11px] text-slate-400 mt-1 shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span>{f.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-5 sm:px-6 pb-5 pl-[3.5rem] sm:pl-[4rem] text-sm text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </SectionContainer>
  );
}
